import React, { createContext, useContext, useState, useCallback } from 'react';
import { ENDPOINTS } from '../endpoints';

const AdminCacheContext = createContext();

export const useAdminCache = () => {
  const context = useContext(AdminCacheContext);
  if (!context) {
    throw new Error('useAdminCache debe ser usado dentro de AdminCacheProvider');
  }
  return context;
};

export const AdminCacheProvider = ({ children }) => {
  // Estados de caché para cada sección
  const [cache, setCache] = useState({
    roles: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false },
    organizations: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false },
    persons: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false },
    competencyFrameworks: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false },
    courses: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false },
    coursesBySubject: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false },
    users: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false }
  });

  // Función para cargar datos de una sección específica
  const loadSectionData = useCallback(async (sectionName, force = false) => {
    const currentCache = cache[sectionName];
    
    // Si ya se intentó cargar, ya está cargando o ya se cargó, no hacer nada
    // EXCEPTO si force = true, entonces ignorar estas validaciones
    if (!force && (currentCache.hasAttemptedLoad || currentCache.loading || currentCache.loaded)) {
      return currentCache;
    }

    // Marcar como "intentado cargar" y como cargando
    setCache(prev => ({
      ...prev,
      [sectionName]: { 
        ...prev[sectionName], 
        hasAttemptedLoad: true,
        loading: true, 
        error: null 
      }
    }));

    try {
      let data = null;
      const token = localStorage.getItem('artemis_token');

      switch (sectionName) {
        case 'roles':
          const rolesResponse = await fetch(ENDPOINTS.ADMIN_ROLES, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (rolesResponse.ok) {
            data = await rolesResponse.json();
          } else {
            throw new Error('Error al cargar los roles');
          }
          break;

        case 'organizations':
          const orgsResponse = await fetch(ENDPOINTS.ADMIN_ORGANIZATIONS, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (orgsResponse.ok) {
            data = await orgsResponse.json();
          } else {
            throw new Error('Error al cargar las organizaciones');
          }
          break;

        case 'persons':
          const personsResponse = await fetch(ENDPOINTS.ADMIN_PERSONS, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (personsResponse.ok) {
            data = await personsResponse.json();
          } else {
            throw new Error('Error al cargar las personas');
          }
          break;

        case 'competencyFrameworks':
          const frameworksResponse = await fetch(ENDPOINTS.ADMIN_COMPETENCY_FRAMEWORKS, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (frameworksResponse.ok) {
            data = await frameworksResponse.json();
          } else {
            throw new Error('Error al cargar los marcos de competencias');
          }
          break;

        case 'courses':
          const coursesResponse = await fetch(ENDPOINTS.ADMIN_COURSES, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (coursesResponse.ok) {
            data = await coursesResponse.json();
          } else {
            throw new Error('Error al cargar los cursos');
          }
          break;

        case 'coursesBySubject':
          // Para coursesBySubject, necesitamos primero los frameworks
          const frameworksForCourses = await fetch(ENDPOINTS.ADMIN_COMPETENCY_FRAMEWORKS, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (frameworksForCourses.ok) {
            const frameworks = await frameworksForCourses.json();
            const uniqueSubjects = [...new Set(frameworks.map(fw => fw.subject))];

            // Obtener cursos
            const coursesForSubjects = await fetch(ENDPOINTS.ADMIN_COURSES, {
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (coursesForSubjects.ok) {
              const allCourses = await coursesForSubjects.json();

              // Obtener asociaciones existentes curso-asignatura EN PARALELO (optimización)
              const associationsPromises = uniqueSubjects.map(async (subject) => {
                try {
                  const associationsResponse = await fetch(ENDPOINTS.ADMIN_COURSES_BY_SUBJECT(subject), {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                  });
                  if (associationsResponse.ok) {
                    const associations = await associationsResponse.json();
                    return { subject, associations };
                  }
                  return { subject, associations: [] };
                } catch (error) {
                  console.error(`Error al cargar asociaciones para ${subject}:`, error);
                  return { subject, associations: [] };
                }
              });
              
              const associationsResults = await Promise.all(associationsPromises);
              
              // Construir el mapa de cursos por asignatura
              const coursesMap = {};
              associationsResults.forEach(({ subject, associations }) => {
                coursesMap[subject] = associations;
              });
              
              data = {
                subjects: uniqueSubjects,
                allCourses: allCourses,
                coursesBySubject: coursesMap
              };
            } else {
              throw new Error('Error al cargar los cursos');
            }
          } else {
            throw new Error('Error al cargar los marcos de competencias');
          }
          break;

        case 'users':
          const usersResponse = await fetch(ENDPOINTS.ADMIN_USERS, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (usersResponse.ok) {
            data = await usersResponse.json();
          } else {
            throw new Error('Error al cargar los usuarios');
          }
          break;

        default:
          throw new Error(`Sección no reconocida: ${sectionName}`);
      }

      // Actualizar caché con datos exitosos
      setCache(prev => ({
        ...prev,
        [sectionName]: { 
          data, 
          loading: false, 
          error: null, 
          loaded: true,
          hasAttemptedLoad: true
        }
      }));

      return { data, loading: false, error: null, loaded: true };

    } catch (error) {
      // Actualizar caché con error
      setCache(prev => ({
        ...prev,
        [sectionName]: { 
          data: null, 
          loading: false, 
          error: error.message, 
          loaded: false,
          hasAttemptedLoad: true
        }
      }));

      return { data: null, loading: false, error: error.message, loaded: false };
    }
  }, [cache]);

  // Función para invalidar caché de una sección
  const invalidateCache = useCallback(async (sectionName, reload = true) => {
    // Resetear el estado del caché PERO mantener los datos antiguos para evitar pestañeo
    // Los datos stale se mostrarán hasta que lleguen los nuevos
    setCache(prev => ({
      ...prev,
      [sectionName]: { 
        ...prev[sectionName],  // Mantener data antigua para evitar UI vacía
        loading: reload,       // Marcar como loading si se va a recargar
        error: null, 
        loaded: false, 
        hasAttemptedLoad: false 
      }
    }));
    
    // Si reload es true, recargar los datos automáticamente
    // Usamos force=true para ignorar las validaciones de caché que podrían ver valores antiguos
    if (reload) {
      return await loadSectionData(sectionName, true);
    }
  }, [loadSectionData]);

  // Función para invalidar todo el caché
  const invalidateAllCache = useCallback(() => {
    setCache({
      roles: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false },
      organizations: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false },
      persons: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false },
      competencyFrameworks: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false },
      courses: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false },
      coursesBySubject: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false },
      users: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false }
    });
  }, []);

  // Función para obtener datos de una sección (carga si es necesario)
  const getSectionData = useCallback(async (sectionName) => {
    const currentCache = cache[sectionName];
    
    if (currentCache.loaded || currentCache.loading) {
      return currentCache;
    }

    return await loadSectionData(sectionName);
  }, [cache, loadSectionData]);

  // Función para forzar la carga de datos de una sección
  const forceLoadSectionData = useCallback(async (sectionName) => {
    // Invalidar primero para forzar la recarga
    setCache(prev => ({
      ...prev,
      [sectionName]: { data: null, loading: false, error: null, loaded: false, hasAttemptedLoad: false }
    }));
    
    // Luego cargar los datos con force=true para ignorar validaciones de caché
    return await loadSectionData(sectionName, true);
  }, [loadSectionData]);

  // Función para actualizar el caché directamente (para optimistic updates)
  const updateCacheData = useCallback((sectionName, updater) => {
    setCache(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        data: typeof updater === 'function' ? updater(prev[sectionName].data) : updater
      }
    }));
  }, []);

  const value = {
    cache,
    loadSectionData,
    getSectionData,
    forceLoadSectionData,
    invalidateCache,
    invalidateAllCache,
    updateCacheData  // Nueva función para optimistic updates
  };

  return (
    <AdminCacheContext.Provider value={value}>
      {children}
    </AdminCacheContext.Provider>
  );
};
